/**
 * Theme catalog — craft presets (brand + guideline systems).
 * Free: use presets. Starter+: save customs via Theme Studio + client DB.
 */

import type { ThemeDefinition } from '@/shared/theme/types/theme.types'
import type { ColorTheme } from '@/shared/types/theme.types'
import {
  mechagoraDarkTheme,
  mechagoraLightTheme,
  midnightSlateDarkTheme,
  midnightSlateLightTheme,
  creamPaperDarkTheme,
  creamPaperLightTheme,
  accessibleSignalsDarkTheme,
  accessibleSignalsLightTheme,
} from '@/shared/theme/systems/presets'
import {
  MECHAGORA_DARK,
  MIDNIGHT_SLATE,
  CREAM_PAPER,
  ACCESSIBLE_SIGNALS,
} from '@/shared/theme/systems/desk-tokens'
// MECHAGORA_DARK used for Accessible Signals chrome (brand presence)

export type PresetThemeId =
  | 'mechagora'
  | 'midnight-slate'
  | 'cream-paper'
  | 'accessible-signals'

export interface ThemeSwatches {
  bg: string
  primary: string
  bull: string
  bear: string
  text: string
}

export interface ThemeCatalogEntry {
  id: PresetThemeId
  name: string
  description: string
  tags: string[]
  recommendedMode: 'light' | 'dark'
  swatches: ThemeSwatches
  light: ThemeDefinition
  dark: ThemeDefinition
}

export const THEME_CATALOG: ThemeCatalogEntry[] = [
  {
    id: 'mechagora',
    name: 'Mechagora',
    description:
      'Brand flagship — trading-viable warm stream. Night: deep espresso bay, champagne metal, forest/soft-red sides. Day: sand bay, ivory panels, rose-gold chrome, deep green/red. Product default.',
    tags: ['brand', 'flagship', 'espresso', 'product'],
    recommendedMode: 'dark',
    swatches: {
      bg: MECHAGORA_DARK.canvas,
      primary: MECHAGORA_DARK.primary,
      bull: MECHAGORA_DARK.bull,
      bear: MECHAGORA_DARK.bear,
      text: MECHAGORA_DARK.textPrimary,
    },
    light: mechagoraLightTheme,
    dark: mechagoraDarkTheme,
  },
  // Midnight Slate retired (Night G2 piece 17). Stored 'midnight-slate' resolves to 'mechagora'.
  {
    id: 'cream-paper',
    name: 'Cream Paper',
    description:
      'Matte paper day desk. Warm bronze chrome (brand-adjacent). Deeper teal/crimson sides for daylight scan.',
    tags: ['daylight', 'reading'],
    recommendedMode: 'light',
    swatches: {
      bg: CREAM_PAPER.canvas,
      primary: CREAM_PAPER.primary,
      bull: CREAM_PAPER.bull,
      bear: CREAM_PAPER.bear,
      text: CREAM_PAPER.textPrimary,
    },
    light: creamPaperLightTheme,
    dark: creamPaperDarkTheme,
  },
  {
    id: 'accessible-signals',
    name: 'Accessible Signals',
    description:
      'Mechagora espresso/sand chrome (champagne metal) with colorblind-safe sides only: teal-cyan bull + amber bear — not red/green, not blue UI. Pair with +/− or Buy/Sell labels.',
    tags: ['a11y', 'colorblind', 'safety', 'brand'],
    recommendedMode: 'dark',
    swatches: {
      bg: MECHAGORA_DARK.canvas,
      primary: MECHAGORA_DARK.primary,
      bull: ACCESSIBLE_SIGNALS.darkBull,
      bear: ACCESSIBLE_SIGNALS.darkBear,
      text: MECHAGORA_DARK.textPrimary,
    },
    light: accessibleSignalsLightTheme,
    dark: accessibleSignalsDarkTheme,
  },
]

export const THEME_REGISTRY: Record<
  PresetThemeId,
  { light: ThemeDefinition; dark: ThemeDefinition }
> = {
  mechagora: { light: mechagoraLightTheme, dark: mechagoraDarkTheme },
  'midnight-slate': {
    light: midnightSlateLightTheme,
    dark: midnightSlateDarkTheme,
  },
  'cream-paper': { light: creamPaperLightTheme, dark: creamPaperDarkTheme },
  'accessible-signals': {
    light: accessibleSignalsLightTheme,
    dark: accessibleSignalsDarkTheme,
  },
}

export const VALID_PRESET_IDS: PresetThemeId[] = THEME_CATALOG.map((e) => e.id)
/** Product default — brand craft, not a guideline pack. */
export const DEFAULT_COLOR_THEME: ColorTheme = 'mechagora'

export function isPresetThemeId(id: unknown): id is PresetThemeId {
  return typeof id === 'string' && (VALID_PRESET_IDS as string[]).includes(id)
}

export function resolveColorTheme(id: unknown): ColorTheme {
  if (isPresetThemeId(id)) return id
  return DEFAULT_COLOR_THEME
}

export function getThemeCatalogEntry(id: string): ThemeCatalogEntry {
  return THEME_CATALOG.find((e) => e.id === id) ?? THEME_CATALOG[0]!
}

export function listThemeCatalog(): ThemeCatalogEntry[] {
  return THEME_CATALOG
}

export const isValidColorTheme = isPresetThemeId
export const VALID_COLOR_THEMES = VALID_PRESET_IDS
